import { parseSupportCredentials } from './assistant-support-validation';
import { computed, effect, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { environment } from '../../../environments/assistant.runtime';
import { ASSISTANT_CONTEXT, assistantContextKey } from './assistant-context';
import { AssistantVoiceService } from './assistant-voice.service';
import { HubCentralChatApiService } from './hub-central-chat-api.service';
import { AssistantSupportCredentials } from './assistant.models';
import {
  SUPPORT_SESSION_FACTORY,
  SupportItem,
  SupportSession,
} from './connect-chat-client';

export interface SupportConfig {
  enabled: boolean;
}
export const SUPPORT_CONFIG = new InjectionToken<SupportConfig>('Live support configuration', {
  providedIn: 'root',
  factory: () => ({ enabled: environment.assistantConnectSupportEnabled === true }),
});

type Phase =
  'idle' | 'starting' | 'waiting' | 'connected' | 'reconnecting' | 'ending' | 'ended' | 'error';
interface Handoff {
  session_id: string;
  request_id: string;
  partner_id: string | null;
  roles: readonly string[];
}
export interface SupportMessage {
  id: string;
  text: string;
  speaker: string;
  role: 'user' | 'agent' | 'system';
  time: string;
  participantId?: string;
  handoffId?: string;
}

@Injectable({ providedIn: 'root' })
export class AssistantSupportService {
  private readonly config = inject(SUPPORT_CONFIG);
  private readonly context = inject(ASSISTANT_CONTEXT);
  private readonly voice = inject(AssistantVoiceService);
  private readonly loadClient = inject(SUPPORT_SESSION_FACTORY);
  private readonly chatApi = inject(HubCentralChatApiService);
  readonly enabled = this.config.enabled;
  readonly phase = signal<Phase>('idle');
  readonly messages = signal<SupportMessage[]>([]);
  readonly notice = signal('');
  readonly agentName = signal('');
  private readonly agentNames = new Map<string, string>();
  readonly sending = signal(false);
  readonly cancelRequested = signal(false);
  readonly endMessage = signal('Live chat ended.');
  private hadAgent = false;
  readonly active = computed(() => !['idle', 'ended'].includes(this.phase()));
  readonly canSend = computed(
    () => ['waiting', 'connected'].includes(this.phase()) && !this.sending(),
  );
  readonly status = computed(
    () =>
      ({
        idle: '',
        starting: 'Connecting you to an agent…',
        waiting: 'Waiting for an agent…',
        connected: this.agentName() ? `Chatting with ${this.agentName()}` : 'Chatting with a live agent',
        reconnecting: 'Reconnecting to support…',
        ending: this.cancelRequested() ? 'Canceling request…' : 'Ending chat…',
        ended: this.endMessage(),
        error: 'Support connection needs attention. End chat before trying again.',
      })[this.phase()],
  );
  private session?: SupportSession;
  private handoff?: Handoff;
  private generation = 0;
  private ownerContext: string | null = null;
  private agentJoined = false;
  private recoveringGeneration: number | null = null;
  private lastStatusTime = '';

  constructor() {
    effect(() => {
      const key = this.contextKey();
      if (this.ownerContext !== null && key !== this.ownerContext) this.reset();
    });
  }

  private isCurrent(generation: number): boolean {
    if (generation !== this.generation) return false;
    if (this.ownerContext !== null && this.ownerContext !== this.contextKey()) {
      this.reset();
      return false;
    }
    return true;
  }

  private contextKey(): string {
    const context = this.context();
    return assistantContextKey(context);
  }

  prepare(sessionId: string, handoffId: string): void {
    if (!this.enabled || this.active()) return;
    if (this.ownerContext !== null && this.ownerContext !== this.contextKey()) this.reset();
    if (this.handoff && this.handoff.session_id !== sessionId) this.reset();
    this.voice.cancel();
    const context = this.context();
    this.handoff = { session_id: sessionId, request_id: handoffId, partner_id: context.partnerId,
      roles: [...context.roles] };
    this.ownerContext = this.contextKey();
    this.hadAgent = false;
    this.cancelRequested.set(false);
    this.notice.set('');
    this.phase.set('starting');
  }

  handoffFailed(): void {
    if (this.phase() === 'starting') {
      this.phase.set('error');
      this.notice.set('Connection could not be confirmed. Please cancel this attempt before trying again.');
    }
  }

  async completeHandoff(
    sessionId: string,
    supplied?: AssistantSupportCredentials,
    pendingId: string | null = null,
  ): Promise<void> {
    if (this.ownerContext !== null && this.ownerContext !== this.contextKey()) {
      this.reset();
      return;
    }
    if (pendingId !== null) {
      if (this.handoff?.request_id !== pendingId || this.handoff.session_id !== sessionId) return;
      if (!supplied) {
        // Preserve the server attempt for cancellation. Never create a second contact,
        // including when the user already canceled before completion arrived.
        this.handoffFailed();
        return;
      }
    }
    await this.start(sessionId, supplied);
  }

  async start(
    sessionId: string,
    supplied?: AssistantSupportCredentials,
  ): Promise<void> {
    // A credential response belongs only to the pending attempt that requested it.
    // Never rebind an old attempt to whichever partner happens to be selected now.
    if (this.ownerContext !== null && this.ownerContext !== this.contextKey()) {
      this.reset();
      return;
    }
    if (supplied) {
      if (!this.handoff || this.handoff.session_id !== sessionId ||
          this.handoff.request_id !== supplied.handoffId) return;
      if (!this.enabled || this.phase() !== 'starting') {
        if (['ending', 'ended', 'idle'].includes(this.phase()))
          await this.sendCommand('end', this.handoff).catch(() => undefined);
        return;
      }
    } else {
      if (!this.enabled) return;
      if (this.active()) return;
      if (this.handoff && this.handoff.session_id !== sessionId) this.reset();
    }
    this.voice.cancel();
    this.notice.set('');
    this.phase.set('starting');
    this.agentJoined = false;
    this.hadAgent = false;
    this.cancelRequested.set(false);
    this.agentName.set('');
    this.agentNames.clear();
    this.lastStatusTime = '';
    const context = this.context();
    this.ownerContext = assistantContextKey(context);
    const generation = ++this.generation;
    const handoff = {
      session_id: sessionId,
      request_id: supplied?.handoffId ?? crypto.randomUUID(),
      partner_id: context.partnerId,
      roles: [...context.roles],
    };
    this.handoff = handoff;
    const currentAttempt = () => this.isCurrent(generation);
    let session: SupportSession | undefined;
    try {
      const factory = await this.loadClient();
      if (!currentAttempt() || this.phase() !== 'starting') return;
      const result = supplied ?? await this.sendCommand('start', handoff);
      if (!currentAttempt() || this.phase() !== 'starting') {
        await this.sendCommand('end', handoff);
        return;
      }
      const { initialContextMessage, contextMessageIds, ...credentials } = parseSupportCredentials(result);
      this.initialContextMessage = initialContextMessage;
      this.contextMessageIds = new Set(contextMessageIds ?? []);
      this.initialContextId = undefined;
      this.customerParticipantId = credentials.participantId;
      session = factory(credentials);
      this.session = session;
      const current = () => currentAttempt() && this.session === session;
      session.onMessage(({ data }) => {
        if (current() && !['idle', 'ended'].includes(this.phase())) this.receive(data);
      });
      session.onEnded(() => {
        if (!current() || this.phase() === 'ending') return;
        this.finish();
        this.session = undefined;
        void this.sendCommand('end', handoff).catch(() => {
          /* Contact already ended; reconcile on server. */
        });
      });
      session.onConnectionLost(() => {
        if (current() && !['idle', 'ended', 'ending'].includes(this.phase())) this.phase.set('reconnecting');
      });
      session.onConnectionBroken(() => {
        if (current() && !['idle', 'ended', 'ending'].includes(this.phase())) this.phase.set('error');
      });
      session.onConnectionEstablished(() => {
        if (!current() || ['idle', 'ending', 'ended'].includes(this.phase())) return;
        this.phase.set(this.agentJoined ? 'connected' : 'waiting');
        void this.recover(session!, generation);
      });
      await session.connect();
      if (!current()) {
        await session.disconnectParticipant().catch(() => undefined);
        return;
      }
      if (this.phase() === 'starting') this.phase.set('waiting');
      await this.recover(session, generation);
    } catch {
      if (!currentAttempt()) return;
      this.notice.set('Could not connect to support. Please end this attempt and try again.');
      this.phase.set('error');
      // Keep the handoff ID for a safe cleanup/retry, including an ambiguous start timeout.
    }
  }

  private initialContextMessage: string | undefined;
  private contextMessageIds = new Set<string>();
  private initialContextId: string | undefined;
  private customerParticipantId: string | undefined;

  private receive(item: SupportItem): void {
    // Suppress only the backend-identified initial context, never a heading match
    // or every customer message. Both live events and recovery use this boundary.
    if (item.Type === 'MESSAGE' && item.ParticipantRole === 'CUSTOMER' && item.Id &&
        (!item.ParticipantId || item.ParticipantId === this.customerParticipantId)) {
      if (this.contextMessageIds.has(item.Id)) return;
      if (item.Id === this.initialContextId) return;
      if (!this.initialContextId && this.initialContextMessage &&
          item.ContentType === 'text/plain' && item.Content === this.initialContextMessage) {
        this.initialContextId = item.Id;
        return;
      }
    }
    if (item.ContentType === 'application/vnd.amazonaws.connect.event.chat.ended') {
      if (this.phase() !== 'ending') this.finish();
      return;
    }
    const handoffId = this.handoff?.request_id;
    const messageId = item.Id && handoffId ? `${handoffId}:${item.Id}` : '';
    const isAgent = item.ParticipantRole === 'AGENT';
    const joined = item.ContentType === 'application/vnd.amazonaws.connect.event.participant.joined';
    const displayName = item.DisplayName?.trim();
    if (isAgent && displayName && item.ParticipantId) {
      this.agentNames.set(item.ParticipantId, displayName);
      this.messages.update(messages => messages.map(message =>
        message.handoffId === handoffId && message.role === 'agent' && message.participantId === item.ParticipantId
          ? { ...message, speaker: displayName } : message));
    }
    const name = displayName || (item.ParticipantId ? this.agentNames.get(item.ParticipantId) : '') || '';
    if (
      item.ParticipantRole === 'AGENT' &&
      (!item.AbsoluteTime || item.AbsoluteTime >= this.lastStatusTime)
    ) {
      this.lastStatusTime = item.AbsoluteTime ?? this.lastStatusTime;
      if (item.ContentType === 'application/vnd.amazonaws.connect.event.participant.left') {
        this.agentJoined = false;
        this.agentName.set('');
        if (!['ended', 'ending'].includes(this.phase())) this.phase.set('waiting');
      } else if (item.Type === 'MESSAGE' || item.ContentType?.endsWith('.participant.joined')) {
        this.hadAgent = true;
        this.agentJoined = true;
        this.agentName.set(name);
        if (!['ended', 'ending'].includes(this.phase())) this.phase.set('connected');
      }
    }
    if (isAgent && joined && item.Id && !this.messages().some(message => message.id === messageId)) {
      this.messages.update(messages => [...messages, {
        id: messageId, handoffId, role: 'system', speaker: 'Support',
        text: name ? `${name} joined the chat.` : 'A live agent joined the chat.',
        time: item.AbsoluteTime ?? '', participantId: item.ParticipantId,
      }].sort((a, b) => a.time.localeCompare(b.time)).slice(-1000) as SupportMessage[]);
      return;
    }
    if (
      item.Type !== 'MESSAGE' ||
      item.ContentType !== 'text/plain' ||
      !item.Id ||
      typeof item.Content !== 'string' ||
      item.Content.length > 20000
    )
      return;
    if (this.messages().some((message) => message.id === messageId)) return;
    const message: SupportMessage = {
      id: messageId, handoffId,
      text: item.Content,
      role: item.ParticipantRole === 'CUSTOMER' ? 'user' : item.ParticipantRole === 'AGENT' ? 'agent' : 'system',
      speaker:
        item.ParticipantRole === 'CUSTOMER'
          ? 'You'
          : item.ParticipantRole === 'AGENT'
            ? name || 'Live agent'
            : 'Support',
      time: item.AbsoluteTime ?? '',
      participantId: item.ParticipantId,
    };
    this.messages.update((messages) =>
      [...messages, message].sort((a, b) => a.time.localeCompare(b.time)).slice(-1000),
    );
  }

  private async recover(session: SupportSession, generation: number): Promise<void> {
    if (this.recoveringGeneration === generation) return;
    this.recoveringGeneration = generation;
    try {
      const items: SupportItem[] = [];
      let nextToken: string | undefined;
      const seenTokens = new Set<string>();
      do {
        if (nextToken) {
          if (seenTokens.has(nextToken)) throw new Error('Repeated transcript page');
          seenTokens.add(nextToken);
        }
        const result = await session.getTranscript({
          maxResults: 100,
          sortOrder: 'ASCENDING',
          nextToken,
        });
        if (!this.isCurrent(generation) || this.session !== session ||
            ['idle', 'ended', 'ending'].includes(this.phase())) return;
        items.push(...(result.data.Transcript ?? []));
        nextToken = result.data.NextToken;
      } while (nextToken && items.length < 1000);
      items
        .sort((a, b) => (a.AbsoluteTime ?? '').localeCompare(b.AbsoluteTime ?? ''))
        .forEach((item) => this.receive(item));
      if (nextToken) this.notice.set('Some earlier support messages could not be loaded.');
    } catch {
      if (this.isCurrent(generation))
        this.notice.set('Some support messages could not be loaded.');
    } finally {
      if (this.recoveringGeneration === generation) this.recoveringGeneration = null;
    }
  }

  async send(text: string): Promise<boolean> {
    text = text.trim();
    if (!this.isCurrent(this.generation)) return false;
    if (!text || text.length > 2000 || !this.canSend() || !this.session) return false;
    const session = this.session,
      generation = this.generation;
    this.sending.set(true);
    this.notice.set('');
    try {
      const result = await session.sendMessage({ contentType: 'text/plain', message: text });
      if (!this.isCurrent(generation)) return false;
      this.receive({
        ...result.data,
        Type: 'MESSAGE',
        ContentType: 'text/plain',
        Content: text,
        ParticipantRole: 'CUSTOMER',
      });
      return true;
    } catch {
      if (this.isCurrent(generation))
        this.notice.set(
          'Message delivery could not be confirmed. Check the conversation before retrying.',
        );
      return false;
    } finally {
      if (this.isCurrent(generation)) this.sending.set(false);
    }
  }

  async end(): Promise<void> {
    if (!this.handoff || ['ending', 'ended'].includes(this.phase())) return;
    const handoff = this.handoff,
      session = this.session,
      generation = this.generation;
    this.cancelRequested.set(!this.hadAgent);
    this.phase.set('ending');
    this.notice.set('');
    try {
      await this.sendCommand('end', handoff);
      if (!this.isCurrent(generation)) return;
      this.session = undefined;
      this.finish(this.cancelRequested() ? 'Connection request canceled.' : 'Live chat ended.');
      void session?.disconnectParticipant().catch(() => undefined);
    } catch {
      if (!this.isCurrent(generation)) return;
      this.phase.set('error');
      this.notice.set(
        this.cancelRequested()
          ? 'Could not confirm cancellation. Please try canceling again.'
          : 'Could not confirm the support chat ended. Please try End chat again.',
      );
    }
  }

  private finish(message = this.hadAgent ? 'Live chat ended.' : 'Connection request ended.'): void {
    if (this.phase() === 'ended') return;
    this.endMessage.set(message);
    this.phase.set('ended');
    const id = `support-ended-${this.handoff?.request_id ?? this.generation}`;
    if (!this.messages().some(message => message.id === id)) {
      this.messages.update(messages => [...messages, {
        id, handoffId: this.handoff?.request_id, text: message, speaker: 'Support', role: 'system' as const,
        time: new Date().toISOString(),
      }].slice(-1000));
    }
  }

  returnToAssistant(): void {
    if (this.phase() === 'ended') {
      this.phase.set('idle');
      this.notice.set('');
    }
  }

  reset(): void {
    const handoff = this.handoff,
      session = this.session;
    ++this.generation;
    this.initialContextMessage = undefined;
    this.contextMessageIds.clear();
    this.initialContextId = undefined;
    this.customerParticipantId = undefined;
    this.handoff = undefined;
    this.session = undefined;
    this.ownerContext = null;
    this.hadAgent = false;
    this.cancelRequested.set(false);
    this.phase.set('idle');
    this.messages.set([]);
    this.agentName.set('');
    this.agentNames.clear();
    this.notice.set('');
    this.sending.set(false);
    if (handoff) void this.sendCommand('end', handoff).catch(() => undefined);
    void session?.disconnectParticipant().catch(() => undefined);
  }

  private sendCommand(action: 'start' | 'end', handoff: Handoff): Promise<unknown> {
    return this.chatApi.support(action === 'start' ? 'startSupport' : 'endSupport',
      handoff.session_id, handoff.request_id,
      { partnerId: handoff.partner_id, roles: handoff.roles });
  }
}
